import { useEffect, useState } from "react";
import { api, fmtINR, monthISO } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFilters } from "@/context/FiltersContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Tooltip, LineChart, Line, XAxis, YAxis, ReferenceLine,
} from "recharts";
import { Award, TrendingDown, TrendingUp } from "lucide-react";

export default function Scorecard() {
  const { user } = useAuth();
  const { executives } = useFilters();
  const [execId, setExecId] = useState("");
  const [month, setMonth] = useState(monthISO());
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState([]);

  useEffect(() => {
    if (user?.role === "executive") setExecId(user.executive_id);
    else if (executives.length) setExecId(executives[0].id);
  }, [user, executives]);

  useEffect(() => {
    if (!execId) return;
    api.get(`/dashboard/scorecard/${execId}`, { params: { month } }).then((r) => setData(r.data));
    api.get(`/dashboard/scorecard-trend/${execId}`, { params: { months: 6 } }).then((r) => setTrend(r.data.trend || []));
  }, [execId, month]);

  if (!data) return <div className="text-slate-400">Loading scorecard…</div>;

  const chartData = data.rows
    .filter((r) => r.performance_pct !== null)
    .map((r) => ({ metric: r.metric.split(" ").slice(0, 2).join(" "), value: Math.min(150, r.performance_pct) }));

  const firstPct = trend[0]?.overall_pct ?? 0;
  const lastPct = trend[trend.length - 1]?.overall_pct ?? 0;
  const delta = +(lastPct - firstPct).toFixed(2);
  const trending = delta >= 0;

  return (
    <div className="space-y-6" data-testid="scorecard-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#C9A24B]">Performance</div>
          <h2 className="text-3xl font-light text-white mt-1" style={{ fontFamily: "Outfit" }}>
            Executive Scorecard
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {user?.role !== "executive" && (
            <Select value={execId} onValueChange={setExecId}>
              <SelectTrigger data-testid="score-exec-select" className="w-56 bg-slate-900 border-slate-700 text-white">
                <SelectValue placeholder="Choose executive" />
              </SelectTrigger>
              <SelectContent className="bg-[#0F172A] border-slate-700 text-white">
                {executives.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <input data-testid="score-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)}
            className="bg-slate-900 border border-slate-700 text-white rounded-lg px-3 py-1.5 text-sm outline-none" />
        </div>
      </div>

      {/* Top cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-[#1E293B] border border-[#C9A24B]/30 rounded-xl p-6 gold-glow">
          <div className="text-[10px] tracking-widest uppercase text-[#C9A24B]">Overall Performance</div>
          <div className="text-5xl font-light text-white mt-3" style={{ fontFamily: "Outfit" }}>{data.overall_pct}%</div>
          <div className={`text-sm mt-2 ${data.overall_pct >= 100 ? "text-emerald-400" : data.overall_pct >= 85 ? "text-amber-400" : "text-rose-400"}`}>
            {data.overall_pct >= 100 ? "🏆 On or above target" : data.overall_pct >= 85 ? "🟡 Close to target" : "🔴 Below target"}
          </div>
        </div>
        <div className="bg-[#1E293B] border border-emerald-500/30 rounded-xl p-6">
          <div className="flex items-center gap-2 text-emerald-400 text-[10px] tracking-widest uppercase">
            <Award size={13} /> Best Metric
          </div>
          <div className="text-white text-lg font-medium mt-3" style={{ fontFamily: "Outfit" }}>{data.best_metric || "—"}</div>
          <div className="text-emerald-400 text-2xl font-light mt-1" style={{ fontFamily: "Outfit" }}>{data.best_pct}%</div>
        </div>
        <div className="bg-[#1E293B] border border-rose-500/30 rounded-xl p-6">
          <div className="flex items-center gap-2 text-rose-400 text-[10px] tracking-widest uppercase">
            <TrendingDown size={13} /> Weakest Metric
          </div>
          <div className="text-white text-lg font-medium mt-3" style={{ fontFamily: "Outfit" }}>{data.weakest_metric || "—"}</div>
          <div className="text-rose-400 text-2xl font-light mt-1" style={{ fontFamily: "Outfit" }}>{data.weakest_pct}%</div>
        </div>
      </div>

      {/* Radar + Table */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 bg-[#1E293B] border border-slate-800 rounded-xl p-6" data-testid="scorecard-radar">
          <div className="text-white font-medium mb-2" style={{ fontFamily: "Outfit" }}>Metric Radar</div>
          <ResponsiveContainer width="100%" height={340}>
            <RadarChart data={chartData} outerRadius={110}>
              <PolarGrid stroke="#334155" />
              <PolarAngleAxis dataKey="metric" tick={{ fill: "#94A3B8", fontSize: 11 }} />
              <PolarRadiusAxis angle={30} domain={[0, 150]} tick={{ fill: "#64748B", fontSize: 10 }} />
              <Tooltip contentStyle={{ backgroundColor: "#0F172A", border: "1px solid #334155", borderRadius: 8, color: "#fff" }} />
              <Radar dataKey="value" stroke="#C9A24B" fill="#C9A24B" fillOpacity={0.35} />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        <div className="lg:col-span-3 bg-[#1E293B] border border-slate-800 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-800/50 text-slate-300 text-[11px] uppercase tracking-wider">
                <th className="text-left px-4 py-3">Metric</th>
                <th className="text-right px-4 py-3">Actual</th>
                <th className="text-right px-4 py-3">Target</th>
                <th className="text-right px-4 py-3">Perf %</th>
                <th className="text-left px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.metric} className="border-t border-slate-800">
                  <td className="px-4 py-2.5 text-slate-200">{r.metric}</td>
                  <td className="px-4 py-2.5 text-right text-white font-medium">
                    {r.metric.includes("Revenue") ? fmtINR(r.actual) : r.actual}
                  </td>
                  <td className="px-4 py-2.5 text-right text-slate-400">
                    {r.metric.includes("Revenue") ? fmtINR(r.target) : r.target}
                  </td>
                  <td className="px-4 py-2.5 text-right font-semibold">
                    {r.performance_pct === null ? "—" : `${r.performance_pct}%`}
                  </td>
                  <td className="px-4 py-2.5">
                    {r.status === "on_target" && <span className="text-emerald-400 text-xs font-semibold">✅ On Target</span>}
                    {r.status === "warning" && <span className="text-amber-400 text-xs font-semibold">🟡 Close</span>}
                    {r.status === "below_target" && <span className="text-rose-400 text-xs font-semibold">🔴 Below</span>}
                    {r.status === "n_a" && <span className="text-slate-500 text-xs">N/A</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* 6-Month Trend */}
      <div className="bg-[#1E293B] border border-slate-800 rounded-xl p-6" data-testid="scorecard-trend">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[10px] tracking-widest uppercase text-slate-400 font-semibold">6-Month Performance Trend</div>
            <div className="text-white text-lg font-medium mt-0.5" style={{ fontFamily: "Outfit" }}>
              Overall %, month by month
            </div>
          </div>
          {trend.length > 1 && (
            <div className={`flex items-center gap-1.5 text-sm font-semibold ${trending ? "text-emerald-400" : "text-rose-400"}`}>
              {trending ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
              {trending ? "+" : ""}{delta}% vs 6 months ago
            </div>
          )}
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={trend}>
            <XAxis dataKey="month" tick={{ fill: "#94A3B8", fontSize: 11 }} tickFormatter={(v) => v.slice(2)} />
            <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} tickFormatter={(v) => `${v}%`} domain={[0, "auto"]} />
            <Tooltip contentStyle={{ backgroundColor: "#0F172A", border: "1px solid #334155", borderRadius: 8, color: "#fff" }}
                     formatter={(v, k) => k === "overall_pct" ? [`${v}%`, "Overall"] : [fmtINR(v), "Revenue"]} />
            <ReferenceLine y={100} stroke="#059669" strokeDasharray="4 4" />
            <Line type="monotone" dataKey="overall_pct" stroke="#C9A24B" strokeWidth={2.5} dot={{ fill: "#C9A24B", r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
        <div className="grid grid-cols-6 gap-2 mt-3">
          {trend.map((t) => (
            <div key={t.month} className="text-center bg-slate-900/50 rounded-lg p-2 border border-slate-800">
              <div className="text-[10px] text-slate-500 uppercase">{t.month.slice(2)}</div>
              <div className={`text-sm font-semibold mt-0.5 ${t.overall_pct >= 100 ? "text-emerald-400" : t.overall_pct >= 85 ? "text-amber-400" : "text-rose-400"}`}>
                {t.overall_pct}%
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5">{fmtINR(t.revenue)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
